import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Network, Users, MessageSquare, Settings as SettingsIcon, RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [e, s] = await Promise.all([api.get("/employees"), api.get("/settings")]);
      setEmployees(e.data);
      setSettings(s.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/microsoft/sync");
      toast.success(`Synced ${data.synced} users${data.mocked ? " (mock)" : ""}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed", { duration: 8000 });
    }
    setLoading(false);
  };

  const total = employees.length;
  const ms = employees.filter((e) => e.source === "microsoft").length;
  const pending = employees.filter((e) => e.sync_status && e.sync_status.startsWith("pending")).length;
  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))].length;

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      {/* Header */}
      <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            {settings?.company_name || "Organization"} · Dashboard
          </p>
          <h1 className="mt-2 text-4xl tracking-tight md:text-5xl" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Hello, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-2 max-w-xl text-sm text-neutral-600">
            Your organization at a glance. Pull from Microsoft, edit, then push back when ready.
          </p>
        </div>
        <Button onClick={handleSync} disabled={loading} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="dashboard-sync-button">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Sync now
        </Button>
      </div>

      {/* Stats grid */}
      <div className="mb-12 grid grid-cols-1 gap-px border border-neutral-200 bg-neutral-200 md:grid-cols-4">
        <StatTile label="Total Employees" value={total} testId="stat-total" />
        <StatTile label="From Microsoft" value={ms} testId="stat-ms" />
        <StatTile label="Departments" value={departments} testId="stat-depts" />
        <StatTile label="Pending Sync" value={pending} highlight={pending > 0} testId="stat-pending" />
      </div>

      {/* Quick links */}
      <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <QuickLink to="/organogram" icon={Network} label="Open Organogram" sub="Visualize hierarchy" testId="qlink-organogram" />
        <QuickLink to="/directory" icon={Users} label="Directory" sub={`${total} people`} testId="qlink-directory" />
        <QuickLink to="/teams" icon={MessageSquare} label="Teams" sub="Channels & meetings" testId="qlink-teams" />
        <QuickLink to="/settings" icon={SettingsIcon} label="Settings" sub="Sync & users" testId="qlink-settings" />
      </div>

      {/* Recent */}
      <div className="border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="text-lg tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Recently added
          </h2>
          <Link to="/directory" className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline" style={{ fontFamily: '"IBM Plex Mono", monospace' }} data-testid="see-all-link">
            View all →
          </Link>
        </div>
        {employees.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-neutral-600">No employees yet.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button onClick={handleSync} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"><RefreshCw size={14} className="mr-2"/>Sync from Microsoft</Button>
              <Link to="/organogram"><Button variant="outline" className="rounded-none border-neutral-300"><Plus size={14} className="mr-2"/>Add manually</Button></Link>
            </div>
          </div>
        ) : (
          <ul>
            {employees.slice(0, 6).map((e) => (
              <li key={e.id} className="flex items-center justify-between border-b border-neutral-100 px-6 py-3 last:border-0">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{e.name}</p>
                  <p className="truncate text-xs text-neutral-600">{e.designation} · {e.department}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  {e.source}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, highlight, testId }) {
  return (
    <div className="bg-white px-6 py-8" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        {label}
      </p>
      <p className={`mt-3 text-4xl tracking-tight ${highlight ? "text-[#D83B01]" : ""}`} style={{ fontFamily: '"Work Sans", sans-serif' }}>
        {value}
      </p>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, sub, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group border border-neutral-200 bg-white p-6 transition-colors hover:border-black">
      <Icon size={20} className="text-[#002FA7]" />
      <p className="mt-4 text-base font-medium tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
        {label}
      </p>
      <p className="mt-1 text-xs text-neutral-600">{sub}</p>
      <p className="mt-4 text-[10px] uppercase tracking-wider text-neutral-400 group-hover:text-black" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        Open →
      </p>
    </Link>
  );
}
